import { defineStore } from 'pinia';
import { watch } from 'vue';
import { ProjectState, ProjectSecret, ProjectSubscription } from "@/types/store";
import type { FieldType } from '@/types/components.d.ts';
import { onSnapshot, collection, doc, query, where, Query, CollectionReference, WhereFilterOp, orderBy as firestoreOrderBy, limit as firestoreLimit, startAfter, Timestamp } from '@/firebase/firestore';
import { firestore } from '@/firebase/firestore';
import { useUserStore } from '@/store/user.store';
import { useApplicationStore } from '@/store/application.store';
import { useFeaturesStore } from '@/store/features.store';
import orderBy from 'lodash/orderBy'
import type { Many } from 'lodash';
import { BulkImportSession, EventAttendeeSchema, ExtendedEventSchema, ProjectEvent, RegistrationApplicant, WebDAVDocument, Group } from '@/types/components'
import { UserField } from '@/types/components';
import { ProjectPackage } from '@/types/store';
import { Product, Price, PaymentLink, StripeShippingRate, StripeMegaProduct, Order, OrderCartItem, ExtendedBuyButtonData } from '@/types/stripe';
import { Schema } from '@/types/views';
import { CloudinaryAsset } from '@/utils/cloudinary';
import { Channel, ChannelMember } from '@/types/channels';
import { getProjectInformation } from '@/store/helpers/project/getProjectInformation';
// Note: isDemoMode logic moved inline
import demoOrdersData from "@/static/demo-user/orders.json";
import demoDonationProducts from "@/static/demo-user/donations.json";
import type { GroupMember } from '@/types/groups';
import { Invitation } from '@/types/invitations';
import { UserValidationErrors } from '@/types/import';
import { MailMessageExtended } from '@/types/mail';
import { ArchiveItem } from '@/types/archive';

/**
 * Normalizes event data to ensure consistent field access for jelloLink.
 * 
 * IMPORTANT: jelloLink should ONLY read from eventSchema for UI fields.
 * eventData is the CalDAV format and should only be used by the webdav package.
 * 
 * Priority order for field extraction:
 * 1. eventSchema (primary source - UI-friendly format)
 * 2. eventData (fallback - CalDAV format, field name mapping required)
 * 
 * We NO LONGER use flat top-level fields to avoid sync issues between
 * flat and nested structures.
 * 
 * @param event - Raw event data from Firestore
 * @returns Normalized event with fields from eventSchema
 */
function normalizeEventData<T extends Record<string, any>>(event: T | null | undefined): T | null {
    if (!event) return null;

    // Get nested structures - these are the source of truth
    const eventSchema = event.eventSchema || {};
    const eventData = event.eventData || {};

    // Extract fields from eventSchema (primary) or eventData (fallback)
    // eventSchema uses: title, description, start_time, end_time, all_day, location
    // eventData uses: summary, description, dtStart, dtEnd, isAllDay, location (string)
    
    const title = eventSchema.title || eventData.summary || '';
    const description = eventSchema.description || eventData.description || '';
    const start_time = eventSchema.start_time || eventData.dtStart || '';
    const end_time = eventSchema.end_time || eventData.dtEnd || '';
    const all_day = eventSchema.all_day ?? eventData.isAllDay ?? false;
    
    // Location: eventSchema.location is an object, eventData.location is a string
    let location = eventSchema.location;
    if (!location && eventData.location) {
        location = {
            description: eventData.location,
            name: eventData.location
        };
    }

    // Return normalized event with fields extracted from nested structures
    return {
        id: event.id,
        createdAt: event.createdAt,
        createdBy: event.createdBy,
        updatedAt: event.updatedAt,
        updatedBy: event.updatedBy,
        // Core event fields from eventSchema
        title,
        description,
        start_time,
        end_time,
        all_day,
        location,
        // Additional eventSchema fields
        organizer: eventSchema.organizer,
        attendees: eventSchema.attendees || event.attendees || [],
        timeZone: eventSchema.timeZone,
        admin_organizer: eventSchema.admin_organizer,
        enable_payments: eventSchema.enable_payments || event.enable_payments,
        payment_details: eventSchema.payment_details || event.payment_details,
        allow_anyone: eventSchema.allow_anyone,
        // Preserve nested structures for editing/saving
        eventData: event.eventData,
        eventSchema: event.eventSchema,
        // Legacy field for backwards compatibility
        summary: title,
    } as unknown as T;
}

// Update defaultState with the missing group-related properties
const defaultState: ProjectState = {
    isDemo: false,
    projectId: '',
    project_variant: 'organizations', // Default all projects to organizations variant
    // IMPORTANT: Default to null so UI does not assume project info exists when no projectId
    projectInfo: null,
    project: null,
    import_sessions: [],
    project_channels: [],
    active_project_channel: null,
    active_project_channel_membership: null,
    active_project_channel_members: [],
    active_project_channel_members_tmp: [],
    project_settings: [],
    project_events: [],
    active_project_event: null,
    active_project_event_attendees: [],
    active_project_event_attendee_invoices: {},
    project_mail: [],
    project_secrets: [],
    project_sheets: [],
    project_media: [],
    project_user_fields: [],
    project_orders: [],
    project_orders_items: {},
    project_group_field: null,
    project_registration_fields: [],
    active_bulk_import_session: null,
    active_bulk_import_session_users: [],
    active_bulk_import_session_errors: [],
    active_bulk_import_session_profile_images: [],
    active_bulk_import_session_user_pairs: null,
    active_bulk_import_session_users_not_in_pair: null,
    active_bulk_import_session_images_not_in_pair: null,
    active_bulk_import_session_users_total: 0,
    bulk_import_sessions_navigation_button_disabled: {
        previous: false,
        next: true,
    },
    displayed_import_error_count: 0,
    project_groups: [],
    active_project_group: null,
    active_project_group_members: [],
    active_project_group_membership: null,
    boundItems: new Set<string>(),
    project_payments_products: [],
    project_payments_shipping_rates: [],
    project_payments_buy_buttons: [],
    project_payments_prices: {},
    project_payments_paymentLinks: {},
    project_payments_images: {},
    unsubscribes: new Map<string, () => void>(), // Map to store unsubscribe functions
    project_subscriptions: [],
    showAddExistingUserToProjectModal: {
        show: false,
        email: null,
        phone: null,
        uid: null
    },
    watchersInitialized: false,
    project_registration_applicants: [],
    project_archive: [],
    webdav: [],
    webhooks: [],
    project_invitations: [],
};

export const useProjectStore = defineStore('project', {
    state: (): ProjectState => ({ ...defaultState }),

    getters: {
        getState: (state) => <K extends keyof ProjectState>(key: K): ProjectState[K] => {
            return state[key];
        },
        
        /**
         * Returns normalized project_events with guaranteed flat fields.
         * Handles both old (nested-only) and new (flat + nested) event formats.
         */
        normalizedProjectEvents: (state): ExtendedEventSchema[] => {
            if (!state.project_events || state.project_events.length === 0) {
                return [];
            }
            return state.project_events
                .map(event => normalizeEventData(event))
                .filter((event): event is ExtendedEventSchema => event !== null);
        },

        /**
         * Returns normalized active_project_event with guaranteed flat fields.
         * Handles both old (nested-only) and new (flat + nested) event formats.
         */
        normalizedActiveProjectEvent: (state) => {
            if (!state.active_project_event) return null;
            return normalizeEventData(state.active_project_event);
        },

        getProduct: (state) => (productId: string) => {
            return state.project_payments_products.find(product => product.id === productId) || null;
        },

        // New getter for filtered import errors
        filteredImportErrors: (state) => {
            if (!state.active_bulk_import_session_errors || state.active_bulk_import_session_errors.length === 0) {
                return [];
            }

            // Return errors that are not resolved and have actual error data
            // Note: Removed user existence check since error userIds might not match current user IDs
            return state.active_bulk_import_session_errors.filter((error: UserValidationErrors) =>
                !error.resolved &&
                error.errors &&
                Object.keys(error.errors).length > 0
            );
        },

        // New getter for the count of filtered errors
        filteredImportErrorCount: (state): number => {
            // Calculate directly from state using the same logic as the filteredImportErrors getter
            if (!state.active_bulk_import_session_errors || state.active_bulk_import_session_errors.length === 0) {
                return 0;
            }

            // Count errors that are not resolved and have actual error data
            // Note: Removed user existence check since error userIds might not match current user IDs
            // This happens when users are filtered out or use different ID formats
            return state.active_bulk_import_session_errors.filter((error: UserValidationErrors) => {
                const notResolved = !error.resolved;
                const hasErrors = error.errors && Object.keys(error.errors).length > 0;
                return notResolved && hasErrors;
            }).length;
        },

        importSpreadsheetIsPublic: (state) => {
            // First check if there's an active import session (could be in active_bulk_import_session or import_sessions)
            const activeSession = state.active_bulk_import_session ||
                (state.import_sessions.length > 0 ? state.import_sessions[0] : null);

            // Get spreadsheet ID from active session
            const spreadsheetId = activeSession?.spreadsheet?.id;

            // Find corresponding sheet in project_sheets
            if (spreadsheetId) {
                const importSheet = state.project_sheets.find(sheet => sheet.sheetId === spreadsheetId);
                return importSheet?.isPublic ?? false;
            }

            return false;
        },

        getPrice: (state) => (priceId: string) => {
            for (const prices of Object.values(state.project_payments_prices)) {
                const price = prices.find(price => price.id === priceId);
                if (price) {
                    return price;
                }
            }
            return null;
        },
        getSubscription: (state) => (subscriptionId: string) => {
            const subscription = state.project_subscriptions.find((s) => s.subscription?.id === subscriptionId);
            if (subscription) {
                return subscription;
            }

        },
        getProductWithDetails: (state) => (productId: string) => {
            const product = state.project_payments_products.find(p => p.id === productId);
            if (product) {
                const prices = state.project_payments_prices[productId] || [];
                const paymentLinks = state.project_payments_paymentLinks[productId] || [];

                // Check if projectImages array exists and has elements
                const projectImages = state.project_payments_images[productId]
                    ? state.project_payments_images[productId].map(img => img.url)
                    : [];

                // Prevent duplicate images by only using product.images if projectImages is empty
                // Or use projectImages if available (since they should be the same)
                const images = projectImages.length > 0 ? projectImages : (product.images || []);

                return {
                    ...product,
                    prices,
                    paymentLinks,
                    images
                };
            }
            return null;
        },
        getOrderWithItem: (state) => (orderId: string | null, sessionId?: string) => {
            try {
                let order;
                if (orderId) {
                    order = state.project_orders.find(p => p.id === orderId);
                } else {
                    if (!sessionId) throw new Error('No sessionId or orderId provided');
                    order = state.project_orders.find(p => p.session.id === sessionId);
                }
                if (order) {
                    const effectiveOrderId = order?.id;
                    const items = state.project_orders_items[effectiveOrderId] || [];
                    return {
                        ...order,
                        items,
                    } as Order;
                }
                return null;
            } catch (e) {
                console.error(e)
            }
        },
        getOrdersWithItem: (state) => () => {
            try {
                return state.project_orders.map(order => {
                    if (order) {
                        const effectiveOrderId = order?.id;
                        const items = state.project_orders_items[effectiveOrderId] || [];
                        return {
                            ...order,
                            items,
                        } as Order;
                    }
                })
            } catch (e) {
                console.error(e);
                return null;
            }
        },
        // In your getters:
        getCollectionDocumentById: (state) => <T = any>(
            collectionKey: keyof ProjectState,
            docId: string,
            removeId: boolean = false,
            removeMetadata: boolean = false
        ): T | null => {
            const collection = state[collectionKey];
            if (!collection) return null;

            let foundDoc: T | undefined;

            // Case 1: If collection is an array, search by .find(...)
            if (Array.isArray(collection)) {
                foundDoc = collection.find((doc: any) => doc.id === docId) as T;
            }
            // Case 2: If collection is an object, it might be:
            //   { [someKey: string]: T[] }  (object of arrays)
            //   or { [someKey: string]: T } (object keyed by docId)
            else if (typeof collection === 'object') {
                // Directly keyed by docId
                if (collection[docId]) {
                    foundDoc = collection[docId] as T;
                } else {
                    // Object of arrays, e.g. { [someKey: string]: T[] }
                    for (const key in collection) {
                        const value = collection[key];
                        if (Array.isArray(value)) {
                            const match = value.find((doc: any) => doc.id === docId);
                            if (match) {
                                foundDoc = match as T;
                                break;
                            }
                        }
                    }
                }
            }

            if (!foundDoc) return null;

            // If the result is an object, strip out system fields
            if (typeof foundDoc === 'object' && foundDoc !== null) {
                // Make a shallow copy to avoid mutating the store
                let docCopy = { ...foundDoc } as Record<string, unknown>;

                // Apply event normalization for event collections
                // This handles both old (nested-only) and new (flat + nested) event formats
                if (collectionKey === 'project_events') {
                    docCopy = normalizeEventData(docCopy) as Record<string, unknown>;
                    if (!docCopy) return null;
                }

                // Remove `id` if requested
                if (removeId) {
                    delete docCopy['id'];
                }

                // Remove metadata fields if requested
                if (removeMetadata) {
                    const metaFields = ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'];
                    metaFields.forEach(field => {
                        delete docCopy[field];
                    });
                }

                return docCopy as T;
            }

            // If for some reason foundDoc is a primitive, just return it as is
            return foundDoc;
        },
        getCollectionDocumentByParam: (state) => <T = any>(
            collectionKey: keyof ProjectState,
            query: { key: string; operator: WhereFilterOp; value: any },
            removeId: boolean = false,
            removeMetadata: boolean = false
        ): T | null => {
            const collection = state[collectionKey];
            if (!collection) return null;

            // Helper to evaluate the condition based on the operator
            const evaluateCondition = (docValue: any, operator: WhereFilterOp, queryValue: any): boolean => {
                switch (operator) {
                    case '==':
                        return docValue === queryValue;
                    case '!=':
                        return docValue !== queryValue;
                    case '<':
                        return docValue < queryValue;
                    case '<=':
                        return docValue <= queryValue;
                    case '>':
                        return docValue > queryValue;
                    case '>=':
                        return docValue >= queryValue;
                    case 'array-contains':
                        return Array.isArray(docValue) ? docValue.includes(queryValue) : false;
                    case 'in':
                        return Array.isArray(queryValue) ? queryValue.includes(docValue) : false;
                    case 'array-contains-any':
                        return Array.isArray(docValue) && Array.isArray(queryValue)
                            ? queryValue.some((val) => docValue.includes(val))
                            : false;
                    case 'not-in':
                        return Array.isArray(queryValue) ? !queryValue.includes(docValue) : false;
                    default:
                        console.warn(`Operator ${operator} not implemented.`);
                        return false;
                }
            };

            let foundDoc: T | undefined;

            // Case 1: If the collection is an array, find the first document that matches the query condition.
            if (Array.isArray(collection)) {
                foundDoc = collection.find((doc: any) =>
                    evaluateCondition(doc[query.key], query.operator, query.value)
                ) as T;
            }
            // Case 2: If the collection is an object (either a direct mapping or an object of arrays)
            else if (typeof collection === 'object') {
                // If querying by the document's id and the collection is a direct mapping:
                if (query.key === 'id' && collection[query.value]) {
                    foundDoc = collection[query.value] as T;
                } else {
                    // Otherwise, iterate over the object keys (which may be arrays)
                    for (const key in collection) {
                        const value = collection[key];
                        if (Array.isArray(value)) {
                            const match = value.find((doc: any) =>
                                evaluateCondition(doc[query.key], query.operator, query.value)
                            );
                            if (match) {
                                foundDoc = match as T;
                                break;
                            }
                        }
                    }
                }
            }

            if (!foundDoc) return null;

            // Optionally strip out system fields if the result is an object.
            if (typeof foundDoc === 'object' && foundDoc !== null) {
                const docCopy = { ...foundDoc } as Record<string, unknown>;

                if (removeId) {
                    delete docCopy['id'];
                }

                if (removeMetadata) {
                    const metaFields = ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'];
                    metaFields.forEach((field) => delete docCopy[field]);
                }

                return docCopy as T;
            }

            return foundDoc;
        }

    },
    actions: {
        setState<K extends keyof ProjectState>(payload: { key: K; value: ProjectState[K] }) {
            this.$patch(state => {
                state[payload.key] = payload.value;
            });
        },

        /**
         * Set the project variant and emit a change event for reactive updates
         */
        setProjectVariant(variant: import('@/types/app-config').AppVariant) {
            console.log(`[ProjectStore] Setting project variant to: ${variant}`);
            this.$patch(state => {
                state.project_variant = variant;
            });

            // Emit a custom event for reactive navigation updates
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('project-variant-changed', {
                    detail: { variant, projectId: this.projectId }
                }));
                console.log(`[ProjectStore] Emitted project-variant-changed event: ${variant}`);
            }
        },

        /**
         * Get the current project variant
         */
        getProjectVariant(): import('@/types/app-config').AppVariant {
            return this.$state.project_variant;
        },

        /**
         * Reset project variant to default
         */
        resetProjectVariant() {
            this.setProjectVariant('organizations');
        },

        setNestedState(path: string, value: any) {
            const keys = path.split('.');
            let stateRef: any = this.$state;
            keys.forEach((key, index) => {
                if (index === keys.length - 1) {
                    stateRef[key] = value;
                } else {
                    if (!stateRef[key]) {
                        this.$patch((state: any) => {
                            state[key] = {};
                        });
                    }
                    stateRef = stateRef[key];
                }
            });
        },
        getNestedState(path: string): any {
            const keys = path.split('.');
            let stateRef: any = this.$state;
            for (const key of keys) {
                if (stateRef && typeof stateRef === 'object' && key in stateRef) {
                    stateRef = stateRef[key];
                } else {
                    return undefined;
                }
            }
            return stateRef;
        },
        reset<K extends keyof ProjectState>(property?: K) {
            if (property) {
                // Unbind the specific property if it's currently bound
                const unsubscribe = this.$state.unsubscribes.get(property as string) as (() => void) | undefined;
                if (unsubscribe) {
                    unsubscribe(); // Call the unsubscribe function to stop listening
                    this.$state.unsubscribes.delete(property as string); // Remove from unsubscribes map
                }
                this.$state.boundItems.delete(property as string); // Remove from boundItems set

                // Reset the specific property to its default value
                this.$state[property] = defaultState[property];
                localStorage.removeItem(property as string);

            } else {
                // Reset all state properties and unbind all listeners
                this.$state.unsubscribes.forEach((unsubscribe: () => void) => {
                    unsubscribe(); // Unsubscribe from Firestore listeners
                });

                // Clear all bound items and unsubscribes
                this.$state.boundItems.clear();
                this.$state.unsubscribes.clear();

                // Reset the entire state
                Object.assign(this.$state, defaultState);
                Object.keys(defaultState).forEach((key) => {
                    localStorage.removeItem(key);
                });
            }
        },

        updateArrayItem(
            payload: { key: string; index: number; value: any }
        ) {
            this.$patch((state: any) => {
                const arr = state[payload.key] as any[];
                if (payload.index >= 0 && payload.index < arr.length) {
                    arr.splice(payload.index, 1, payload.value);
                }
            });
        },

        addArrayItem(
            payload: { key: string; value: any }
        ) {
            this.$patch((state: any) => {
                const arr = state[payload.key] as any[];
                arr.push(payload.value);
            });
        },

        removeArrayItem(
            payload: { key: string; index: number }
        ) {
            this.$patch((state: any) => {
                const arr = state[payload.key] as any[];
                if (payload.index >= 0 && payload.index < arr.length) {
                    arr.splice(payload.index, 1);
                }
            });
        },

        async bindProjectCollection<T>(
            collectionName: string,
            stateKey: keyof ProjectState,
            sortCriteria?: [string, Many<boolean | "asc" | "desc">][],
            filterField?: string,
            filterOperator?: WhereFilterOp,
            filterValue?: any,
            requiresAuthUser?: boolean,
        ): Promise<void> {
            console.log(`[ProjectStore.bindProjectCollection] 🔍 Starting bind for ${collectionName}:`, {
                projectId: this.$state.projectId,
                stateKey,
                filterField,
                filterOperator,
                filterValue,
                requiresAuthUser,
                hasExistingListener: this.$state.unsubscribes?.has(String(stateKey))
            });
            
            // Check demo mode Firebase operation blocking
            const { useDemo } = await import('@/composables/useDemo');
            const demo = useDemo();

            if (demo.isDemoMode.value && !demo.shouldAllowFirebaseOperation.value) {
                console.warn(`[ProjectStore.bindProjectCollection] ⚠️ Firebase operation blocked in demo mode: bindProjectCollection(${collectionName})`);
                return;
            }

            let authUser = useUserStore().authUser as any;

            const effectiveRequiresAuthUser = requiresAuthUser !== false;
            if (effectiveRequiresAuthUser) {
                if (!authUser || !authUser.uid) {
                    try {
                        const { Auth } = await import('@/firebase/auth');
                        if (Auth?.currentUser?.uid) {
                            authUser = Auth.currentUser;
                            console.log(`[ProjectStore.bindProjectCollection] 🔐 Auth user obtained from Firebase Auth for ${collectionName}`);
                        }
                    } catch (e) {
                        console.warn(`[ProjectStore.bindProjectCollection] ⚠️ Auth import failed while deferring bindProjectCollection for ${collectionName}:`, e);
                    }
                }
                if (!authUser || !authUser.uid) {
                    console.warn(`[ProjectStore.bindProjectCollection] ❌ No authenticated user. Deferring bind for ${collectionName}.`);
                    return;
                }
            }

            console.log(`[ProjectStore.bindProjectCollection] 🔓 Auth check passed for ${collectionName}:`, {
                hasAuthUser: !!authUser,
                authUid: authUser?.uid,
                requiresAuthUser: effectiveRequiresAuthUser
            });

            // Validate that we have a valid projectId
            if (!this.$state.projectId || this.$state.projectId.trim() === '') {
                console.error(`[ProjectStore.bindProjectCollection] ❌ No valid project ID. Cannot bind collection ${collectionName}.`, {
                    projectId: this.$state.projectId,
                    trimmedProjectId: this.$state.projectId?.trim()
                });
                return;
            }

            const boundKey = `${stateKey}`;
            // Check if we have an active listener (unsubscribe function) rather than just boundItems
            // This prevents skipping the real data fetch when we only have cached/demo data
            if (this.$state.unsubscribes.has(boundKey)) {
                console.log(`[ProjectStore.bindProjectCollection] ℹ️ Collection ${collectionName} already has active listener, skipping`);
                return;
            }

            return new Promise((resolve, reject) => {
                const collectionPath = `projects/${this.$state.projectId}/${collectionName}`;
                console.log(`[ProjectStore.bindProjectCollection] 📁 Creating Firebase reference for: ${collectionPath}`);
                
                let firebaseRef: CollectionReference | Query = collection(firestore, 'projects', this.$state.projectId, collectionName);
                if (filterField && filterValue !== undefined && filterOperator) {
                    console.log(`[ProjectStore.bindProjectCollection] 🔍 Applying filter:`, {
                        field: filterField,
                        operator: filterOperator,
                        value: filterValue
                    });
                    firebaseRef = query(firebaseRef, where(filterField, filterOperator, filterValue));
                }

                console.log(`[ProjectStore.bindProjectCollection] 🔄 Setting up onSnapshot listener for ${collectionName}...`);
                
                const unsubscribe = onSnapshot(firebaseRef, 
                    (snapshot) => {
                        console.log(`[ProjectStore.bindProjectCollection] 📊 Snapshot received for ${collectionName}:`, {
                            size: snapshot.size,
                            empty: snapshot.empty,
                            metadata: {
                                fromCache: snapshot.metadata.fromCache,
                                hasPendingWrites: snapshot.metadata.hasPendingWrites
                            }
                        });
                        
                        let data: T[] = [];
                        snapshot.forEach((doc) => {
                            data.push({ id: doc.id, ...doc.data() } as T);
                        });

                        if (sortCriteria && sortCriteria.length > 0) {
                            const iteratees = sortCriteria.map(([field]) => field);
                            const orders = sortCriteria.map(([, order]) => order as boolean | "asc" | "desc"); // Explicitly casting to the expected type

                            data = orderBy(data, iteratees, orders);
                        }
                        
                        console.log(`[ProjectStore.bindProjectCollection] ✅ Successfully bound ${collectionName}:`, {
                            itemCount: data.length,
                            firstItem: data[0],
                            stateKey,
                            boundKey
                        });
                        
                        // Special detailed logging for settings collection
                        if (collectionName === 'settings') {
                            console.log(`[ProjectStore.bindProjectCollection] 📋 Settings details:`, {
                                allSettings: data.map((item: any) => ({
                                    id: item.id,
                                    type: item.type,
                                    disabled: item.disabled,
                                    position: item.position,
                                    containers: item.containers?.length || 0,
                                    hasContent: !!item.containers
                                })),
                                enabledSettings: data.filter((item: any) => !item.disabled).length,
                                sectionsWithContent: data.filter((item: any) => item.containers && item.containers.length > 0).length
                            });
                        }
                        
                        this.setState({ key: stateKey, value: data });
                        this.$state.boundItems.add(boundKey);

                        // Mark import users errors as loaded when binding the errors collection
                        try {
                            const isImportErrorsCollection =
                                typeof collectionName === 'string' &&
                                collectionName.includes('import_sessions/') &&
                                collectionName.endsWith('/errors');
                            if (isImportErrorsCollection) {
                                const applicationStore = useApplicationStore();
                                applicationStore.setState({ key: 'importUsersErrorsLoaded', value: true });
                            }
                        } catch (_) {
                            // No-op: defensive try/catch to avoid impacting other binds
                        }
                        resolve();
                    }, 
                    (error: any) => {
                        console.error(`[ProjectStore.bindProjectCollection] ❌ Error binding ${collectionName}:`, {
                            collectionPath,
                            error: error.message || error,
                            code: error.code,
                            details: error.details,
                            stack: error.stack,
                            projectId: this.$state.projectId,
                            filterField,
                            filterOperator,
                            filterValue
                        });
                        reject(error);
                    }
                );

                this.$state.unsubscribes.set(boundKey, unsubscribe);
                console.log(`[ProjectStore.bindProjectCollection] 🔔 Listener registered for ${collectionName}`);
            });
        },
        async bindProjectCollectionNested<T>(
            collectionName: string,
            nestedKey: string,
            sortCriteria?: [string, Many<boolean | "asc" | "desc">][],
            filterField?: string,
            filterOperator?: WhereFilterOp,
            filterValue?: any
        ): Promise<void> {
            let authUser = useUserStore().authUser as any;
            if (!authUser || !authUser.uid) {
                try {
                    const { Auth } = await import('@/firebase/auth');
                    if (Auth?.currentUser?.uid) {
                        authUser = Auth.currentUser;
                    }
                } catch (e) {
                    console.warn('Auth import failed while deferring bindProjectCollectionNested:', e);
                }
            }
            if (!authUser || !authUser.uid) {
                console.warn(`No authenticated user (yet). Deferring bind for project ${collectionName}.`);
                return;
            }

            // Validate that we have a valid projectId
            if (!this.$state.projectId || this.$state.projectId.trim() === '') {
                console.error(`No valid project ID. Cannot bind project collection ${collectionName}.`);
                return;
            }

            return new Promise((resolve, reject) => {
                let firebaseRef: CollectionReference | Query = collection(firestore, 'projects', this.$state.projectId, collectionName);
                if (filterField && filterValue !== undefined && filterOperator) {
                    firebaseRef = query(firebaseRef, where(filterField, filterOperator, filterValue));
                }

                const unsubscribe = onSnapshot(firebaseRef, (snapshot) => {
                    let data: T[] = [];
                    snapshot.forEach((doc) => {
                        data.push({ id: doc.id, ...doc.data() } as T);
                    });
                    if (sortCriteria && sortCriteria.length > 0) {
                        const iteratees = sortCriteria.map(([field]) => field);
                        const orders = sortCriteria.map(([, order]) => order as boolean | "asc" | "desc"); // Explicitly casting to the expected type

                        data = orderBy(data, iteratees, orders);
                    }
                    this.setNestedState(nestedKey, data);
                    resolve();
                }, (error) => {
                    console.error(`Error binding project ${collectionName}:`, error);
                    reject(error);
                });

                this.$state.unsubscribes.set(nestedKey, unsubscribe);
            });
        },
        async bindProjectCollectionNestedWithPagination<T>(
            collectionName: string,
            nestedKey: string,
            sortCriteria?: [string, Many<boolean | "asc" | "desc">][],
            pageSize: number = 100,
            filterField?: string,
            filterOperator?: WhereFilterOp,
            filterValue?: any,
            sortingFn?: (data: T[]) => T[]
        ): Promise<void> {
            console.log(`🔍 [bindProjectCollectionNestedWithPagination] ENTRY`, {
                collectionName,
                nestedKey,
                sortCriteria,
                pageSize,
                filterField,
                filterValue
            });

            // Check if we have an active listener already - prevent duplicate bindings
            if (this.$state.unsubscribes.has(nestedKey)) {
                console.log(`⏭️ [bindProjectCollectionNestedWithPagination] Already has active listener, SKIPPING`, { nestedKey });
                return;
            }

            let authUser = useUserStore().authUser as any;
            console.log(`🔍 [bindProjectCollectionNestedWithPagination] Auth check 1:`, { 
                hasAuthUser: !!authUser, 
                authUserUid: authUser?.uid 
            });
            
            if (!authUser || !authUser.uid) {
                try {
                    const { Auth } = await import('@/firebase/auth');
                    console.log(`🔍 [bindProjectCollectionNestedWithPagination] Auth check 2 (Firebase Auth):`, { 
                        hasAuth: !!Auth,
                        currentUserUid: Auth?.currentUser?.uid 
                    });
                    if (Auth?.currentUser?.uid) {
                        authUser = Auth.currentUser;
                    }
                } catch (e) {
                    console.warn('Auth import failed while deferring bindProjectCollectionNestedWithPagination:', e);
                }
            }
            if (!authUser || !authUser.uid) {
                console.warn(`❌ [bindProjectCollectionNestedWithPagination] No authenticated user - RETURNING EARLY`, { collectionName });
                return;
            }

            // Validate that we have a valid projectId
            if (!this.$state.projectId || this.$state.projectId.trim() === '') {
                console.error(`❌ [bindProjectCollectionNestedWithPagination] No valid project ID - RETURNING EARLY`, { collectionName });
                return;
            }

            const fullPath = `projects/${this.$state.projectId}/${collectionName}`;
            console.log(`📍 [bindProjectCollectionNestedWithPagination] Setting up Firestore listener`, {
                fullPath,
                projectId: this.$state.projectId,
                collectionName,
                pageSize
            });

            return new Promise((resolve, reject) => {
                // Handle nested collection paths by splitting on '/' and spreading as separate arguments
                // e.g., 'events/eventId/attendees' becomes ['events', 'eventId', 'attendees']
                const pathSegments = collectionName.split('/').filter(segment => segment.length > 0);
                console.log(`📍 [bindProjectCollectionNestedWithPagination] Path segments:`, pathSegments);
                
                let firebaseRef: CollectionReference | Query = collection(firestore, 'projects', this.$state.projectId, ...pathSegments);

                // Apply filters
                if (filterField && filterValue !== undefined && filterOperator) {
                    firebaseRef = query(firebaseRef, where(filterField, filterOperator, filterValue));
                    console.log(`📍 [bindProjectCollectionNestedWithPagination] Applied filter:`, { filterField, filterOperator, filterValue });
                }

                // Apply sorting (required for pagination)
                if (sortCriteria && sortCriteria.length > 0) {
                    const orderConstraints = sortCriteria.map(([field, direction]) =>
                        firestoreOrderBy(field, direction as 'asc' | 'desc')
                    );
                    firebaseRef = query(firebaseRef, ...orderConstraints);
                    console.log(`📍 [bindProjectCollectionNestedWithPagination] Applied sorting:`, sortCriteria);
                } else {
                    console.log(`📍 [bindProjectCollectionNestedWithPagination] No sorting applied (undefined sortCriteria)`);
                }

                // Apply pagination limit
                firebaseRef = query(firebaseRef, firestoreLimit(pageSize));
                console.log(`📍 [bindProjectCollectionNestedWithPagination] Applied limit:`, pageSize);

                console.log(`🚀 [bindProjectCollectionNestedWithPagination] Setting up onSnapshot listener...`);

                const unsubscribe = onSnapshot(firebaseRef, (snapshot) => {
                    console.log(`✅ [bindProjectCollectionNestedWithPagination] onSnapshot callback received`, {
                        collectionName,
                        docsCount: snapshot.docs.length,
                        empty: snapshot.empty
                    });

                    const data: T[] = [];
                    snapshot.forEach((doc) => {
                        data.push({ id: doc.id, ...doc.data() } as T);
                    });

                    // Log first document for debugging
                    if (data.length > 0) {
                        console.log(`📄 [bindProjectCollectionNestedWithPagination] First document sample:`, {
                            id: (data[0] as Record<string, any>)?.id,
                            keys: Object.keys(data[0] || {})
                        });
                    }

                    // Store pagination metadata
                    const paginationKey = `${nestedKey}_pagination`;
                    this.setNestedState(paginationKey, {
                        hasMore: snapshot.docs.length === pageSize,
                        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
                        currentCount: snapshot.docs.length,
                        pageSize
                    });

                    this.setNestedState(nestedKey, sortingFn ? sortingFn(data) : data);
                    this.$state.boundItems.add(nestedKey);
                    console.log(`✅ [bindProjectCollectionNestedWithPagination] Data set in state:`, {
                        nestedKey,
                        itemCount: data.length,
                        addedToBoundItems: true
                    });
                    resolve();
                }, (error) => {
                    console.error(`❌ [bindProjectCollectionNestedWithPagination] onSnapshot ERROR:`, {
                        collectionName,
                        error: error.message,
                        code: (error as any).code
                    });
                    reject(error);
                });

                this.$state.unsubscribes.set(nestedKey, unsubscribe);
                console.log(`📍 [bindProjectCollectionNestedWithPagination] Listener registered in unsubscribes map`);
            });
        },
        async loadMoreEventAttendees(eventId: string): Promise<boolean> {
            const stateKey = 'active_project_event_attendees';
            const paginationKey = `${stateKey}_pagination`;
            const paginationData = this.getNestedState(paginationKey);

            if (!paginationData || !paginationData.hasMore || !paginationData.lastDoc) {
                console.log('[ProjectStore] No more attendees to load');
                return false;
            }

            const authUser = useUserStore().authUser;
            if (!authUser || !authUser.uid) {
                console.error('No authenticated user. Cannot load more attendees.');
                return false;
            }

            if (!this.$state.projectId || this.$state.projectId.trim() === '') {
                console.error('No valid project ID. Cannot load more attendees.');
                return false;
            }

            console.log(`[ProjectStore] Loading more attendees for event ${eventId}`);

            try {
                // Use separate path segments for nested collection
                let firebaseRef: CollectionReference | Query = collection(firestore, 'projects', this.$state.projectId, 'events', eventId, 'attendees');

                // Apply sorting (same as initial load)
                firebaseRef = query(firebaseRef, firestoreOrderBy('createdAt', 'asc'));

                // Start after the last document
                firebaseRef = query(firebaseRef, startAfter(paginationData.lastDoc));

                // Apply pagination limit
                firebaseRef = query(firebaseRef, firestoreLimit(paginationData.pageSize));

                return new Promise((resolve, reject) => {
                    const unsubscribe = onSnapshot(firebaseRef, (snapshot) => {
                        const newData: EventAttendeeSchema[] = [];
                        snapshot.forEach((doc) => {
                            newData.push({ id: doc.id, ...doc.data() } as EventAttendeeSchema);
                        });

                        if (newData.length > 0) {
                            // Append new data to existing
                            const existingData = this.getNestedState(stateKey) || [];
                            const combinedData = [...existingData, ...newData];
                            this.setNestedState(stateKey, combinedData);

                            // Update pagination metadata
                            this.setNestedState(paginationKey, {
                                ...paginationData,
                                hasMore: snapshot.docs.length === paginationData.pageSize,
                                lastDoc: snapshot.docs[snapshot.docs.length - 1] || paginationData.lastDoc,
                                currentCount: combinedData.length
                            });

                            console.log(`[ProjectStore] Loaded ${newData.length} more attendees (total: ${combinedData.length})`);
                        }

                        // Clean up this listener since it's a one-time load
                        unsubscribe();
                        resolve(newData.length > 0);
                    }, (error) => {
                        console.error('Error loading more attendees:', error);
                        unsubscribe();
                        reject(error);
                    });
                });
            } catch (error) {
                console.error('Error setting up query for more attendees:', error);
                return false;
            }
        },
        async bindProjectDocument(
            documentPath: string,
            stateKey: keyof ProjectState,
            usePolling: boolean = false
        ) {
            let authUser = useUserStore().authUser as any;
            if (!authUser || !authUser.uid) {
                try {
                    const { Auth } = await import('@/firebase/auth');
                    if (Auth?.currentUser?.uid) {
                        authUser = Auth.currentUser;
                    }
                } catch (e) {
                    console.warn('Auth import failed while deferring bindProjectDocument:', e);
                }
            }
            if (!authUser || !authUser.uid) {
                console.warn(
                    `No authenticated user (yet). Deferring bind for project document at path ${documentPath}.`
                );
                return;
            }

            // Validate that we have a valid projectId
            if (!this.$state.projectId || this.$state.projectId.trim() === '') {
                console.error(`No valid project ID. Cannot bind project document at path ${documentPath}.`);
                return;
            }

            const boundKey = `${stateKey}`;
            // Check if we have an active listener (unsubscribe function) rather than just boundItems
            // This prevents skipping the real data fetch when we only have cached/demo data
            if (this.$state.unsubscribes.has(boundKey)) {
                console.log(`[ProjectStore] Document ${documentPath} already has active listener, skipping`);
                return;
            }

            // If documentPath doesn't start with "projects/", prefix it with "projects/{projectId}/"
            if (!documentPath.startsWith('projects/')) {
                documentPath = `projects/${this.$state.projectId}/${documentPath}`;
            }

            // Now documentPath should be in the form "projects/{projectId}/..."
            const pathParts = documentPath.split('/');
            const projectId = pathParts[1]; // since pathParts[0] is 'projects'
            const params = pathParts.slice(2);

            console.log(
                'binding project document',
                projectId,
                params
            );

            if (usePolling) {
                // Set up an interval to periodically fetch the latest data
                const fetchData = async () => {
                    try {
                        const data = await getProjectInformation(projectId, params);
                        console.log('data', data);
                        if (data) {
                            this.setState({ key: stateKey, value: data });
                        } else {
                            this.setState({ key: stateKey, value: null });
                        }
                        this.$state.boundItems.add(boundKey);
                    } catch (error) {
                        console.error(
                            `Error binding project document for ${projectId} ${params} :`,
                            error
                        );
                    }
                };

                // Initial fetch
                await fetchData();

                // Set up polling interval (e.g., every 30 seconds)
                const intervalId = setInterval(fetchData, 30000);

                // Store the interval cleanup function
                this.$state.unsubscribes.set(boundKey, () => clearInterval(intervalId));
            } else {
                // Use Firestore's onSnapshot for real-time updates
                const docRef = doc(firestore, documentPath);

                const unsubscribe = onSnapshot(
                    docRef,
                    (docSnapshot) => {
                        if (docSnapshot.exists()) {
                            const data = { id: docSnapshot.id, ...docSnapshot.data() };
                            this.setState({ key: stateKey, value: data });
                            this.$state.boundItems.add(boundKey);
                        } else {
                            this.setState({ key: stateKey, value: null });
                            this.$state.boundItems.add(boundKey);
                        }
                    },
                    (error) => {
                        console.error(
                            `Error binding project document for ${projectId} ${params} ${documentPath}:`,
                            error
                        );
                    }
                );

                this.$state.unsubscribes.set(boundKey, unsubscribe);
            }
        },
        async bindProjectSecrets() {
            await this.bindProjectCollection<ProjectSecret>('secrets', 'project_secrets');
        },
        async bindProjectSettings() {
            console.log('[ProjectStore.bindProjectSettings] 🎯 Starting settings bind:', {
                projectId: this.$state.projectId,
                currentSettingsCount: this.$state.project_settings?.length || 0,
                boundItems: Array.from(this.$state.boundItems || []),
                hasUnsubscribe: this.$state.unsubscribes?.has('project_settings')
            });
            
            try {
                console.log('[ProjectStore.bindProjectSettings] 📊 Calling bindProjectCollection with params:', {
                    collectionName: 'settings',
                    stateKey: 'project_settings',
                    sortCriteria: [['position', 'asc']],
                    filterField: 'type',
                    filterOperator: '==',
                    filterValue: 'section'
                });
                
                await this.bindProjectCollection<Schema[]>(
                    'settings',
                    'project_settings',
                    [['position', 'asc']],
                    'type',  // The field you want to filter by
                    '==', // The filter operator
                    'section'      // The value you want to filter for
                );
                
                console.log('[ProjectStore.bindProjectSettings] ✅ Successfully bound settings:', {
                    settingsCount: this.$state.project_settings?.length || 0,
                    firstSetting: this.$state.project_settings?.[0],
                    boundItems: this.$state.boundItems.has('project_settings')
                });
            } catch (error: any) {
                console.warn('[ProjectStore.bindProjectSettings] ❌ Failed to bind project settings:', {
                    error: error.message || error,
                    code: error.code,
                    details: error.details,
                    stack: error.stack,
                    projectId: this.$state.projectId
                });
                
                // For private label sites, the settings subcollection might not exist
                // Set an empty array to prevent issues
                this.setState({ key: 'project_settings', value: [] });
                this.$state.boundItems.add('project_settings');
                
                console.log('[ProjectStore.bindProjectSettings] 🔄 Set empty settings array as fallback');
                
                // Don't re-throw the error - let the app continue
            }
        },
        async bindProjectRegistrationFields() {
            await this.bindProjectCollection<FieldType>(
                'registration_fields',
                'project_registration_fields',
                [['position', 'asc']], undefined, undefined, undefined, false
            );
        },
        async bindProjectRegistrationApplicants() {
            // Combine both private and public registration applicant sources in real-time
            const stateKey = 'project_registration_applicants' as keyof ProjectState;

            // Check if we have an active listener (unsubscribe function) rather than just boundItems
            // This prevents skipping the real data fetch when we only have cached/demo data
            if (this.$state.unsubscribes.has(stateKey as string)) {
                console.log(`[ProjectStore] Nested collection already has active listener, skipping`);
                return;
            }

            // Validate that we have a valid projectId
            if (!this.$state.projectId || this.$state.projectId.trim() === '') {
                console.error('No valid project ID. Cannot bind project registration applicants.');
                return;
            }

            const projectId = this.$state.projectId;

            // Local caches for merging results from both collections
            let privateApplicants: RegistrationApplicant[] = [];
            let publicApplicants: RegistrationApplicant[] = [];

            const applyMerged = () => {
                // Prefer private collection entries when IDs collide
                const mergedMap = new Map<string, RegistrationApplicant>();
                publicApplicants.forEach((a: any) => {
                    if (a && a.id) mergedMap.set(a.id as string, a);
                });
                privateApplicants.forEach((a: any) => {
                    if (a && a.id) mergedMap.set(a.id as string, a);
                });
                let merged = Array.from(mergedMap.values());
                merged = orderBy(merged, ['createdAt'], ['desc']);
                this.setState({ key: stateKey, value: merged as any });
            };

            const privateRef = collection(firestore, 'projects', projectId, 'registration_applicants');
            const publicRef = collection(firestore, 'projects', projectId, 'public_registration_applicants');

            const unsubPrivate = onSnapshot(
                privateRef,
                (snapshot) => {
                    privateApplicants = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as RegistrationApplicant[];
                    applyMerged();
                },
                (error) => {
                    console.error('Error binding registration_applicants:', error);
                }
            );

            const unsubPublic = onSnapshot(
                publicRef,
                (snapshot) => {
                    publicApplicants = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as RegistrationApplicant[];
                    applyMerged();
                },
                (error) => {
                    console.error('Error binding public_registration_applicants:', error);
                }
            );

            // Store a composed unsubscribe and mark as bound
            this.$state.unsubscribes.set(stateKey as string, () => {
                try { unsubPrivate(); } catch (error) {
                    console.error('Error unsubscribing from private registration applicants:', error);
                }
                try { unsubPublic(); } catch (error) {
                    console.error('Error unsubscribing from public registration applicants:', error);
                }
            });
            this.$state.boundItems.add(stateKey as string);
        },
        async bindBulkImportSessions() {
            await this.bindProjectCollection<BulkImportSession[]>(
                'import_sessions',
                'import_sessions',
                [['createdAt', 'asc']]
            );
        },
        async bindProjectSheets() {
            await this.bindProjectCollection<any[]>(
                'sheets',
                'project_sheets',
                [['createdAt', 'asc']]
            );
        },
        async bindProjectUserFields() {
            console.log('🔥 [bindProjectUserFields] Starting to bind user fields for project:', this.projectId);
            await this.bindProjectCollection<UserField[]>(
                'user_fields',
                'project_user_fields',
                [['id', 'asc']],
                undefined,  // Remove the jello filter
                undefined,
                undefined
            );
            console.log('🔥 [bindProjectUserFields] Finished binding, user fields count:', this.project_user_fields?.length || 0);
            console.log('🔥 [bindProjectUserFields] User fields data:', this.project_user_fields);

            // After binding user fields, set the project group field
            this.setProjectGroupField();
        },
        async bindProjectMedia() {
            await this.bindProjectCollection<CloudinaryAsset[]>(
                'media',
                'project_media',
                [['createdAt', 'asc']]
            );
        },
        async bindProjectWebDAV() {
            await this.bindProjectCollection<WebDAVDocument[]>(
                'webdav',
                'webdav',
                [['createdAt', 'asc']]
            );
        },
        async bindProjectChannels() {
            await this.bindProjectCollection<Channel[]>(
                'channels',
                'project_channels',
                [['createdAt', 'asc']]
            );
        },
        async bindRootCollection<T>(
            collectionName: string,
            stateKey: keyof ProjectState,
            sortCriteria?: [string, Many<boolean | "asc" | "desc">][],
            filterField?: string,
            filterOperator?: WhereFilterOp,
            filterValue?: any,
            requiresAuthUser?: boolean,
        ): Promise<void> {
            const authUser = useUserStore().authUser;

            const effectiveRequiresAuthUser = requiresAuthUser !== false;
            if (effectiveRequiresAuthUser) {
                if (!authUser || !authUser.uid) {
                    console.error(`No authenticated user. Cannot bind root collection ${collectionName}.`);
                    return;
                }
            }

            const boundKey = `${stateKey}`;
            // Check if we have an active listener (unsubscribe function) rather than just boundItems
            // This prevents skipping the real data fetch when we only have cached/demo data
            if (this.$state.unsubscribes.has(boundKey)) {
                console.log(`[ProjectStore] Root collection ${collectionName} already has active listener, skipping`);
                return;
            }

            return new Promise((resolve, reject) => {
                let firebaseRef: CollectionReference | Query = collection(firestore, collectionName);
                if (filterField && filterValue !== undefined && filterOperator) {
                    firebaseRef = query(firebaseRef, where(filterField, filterOperator, filterValue));
                }

                const unsubscribe = onSnapshot(firebaseRef, (snapshot) => {
                    let data: T[] = [];
                    snapshot.forEach((doc) => {
                        data.push({ id: doc.id, ...doc.data() } as T);
                    });

                    if (sortCriteria && sortCriteria.length > 0) {
                        const iteratees = sortCriteria.map(([field]) => field);
                        const orders = sortCriteria.map(([, order]) => order as boolean | "asc" | "desc");

                        data = orderBy(data, iteratees, orders);
                    }
                    this.setState({ key: stateKey, value: data });
                    this.$state.boundItems.add(boundKey);
                    resolve();
                }, (error) => {
                    console.error(`Error binding root collection ${collectionName}:`, error);
                    reject(error);
                });

                this.$state.unsubscribes.set(boundKey, unsubscribe);
            });
        },
        async bindProjectInvitations() {
            // Validate that we have a valid projectId
            if (!this.projectId || this.projectId.trim() === '') {
                console.error(`No valid project ID. Cannot bind project invitations.`);
                return;
            }
            return this.bindRootCollection<Invitation[]>('invitations', 'project_invitations', [['createdAt', 'desc']], 'projectId', '==', this.projectId);
        },
        async bindActiveProjectChannel(channelId: string) {
            if (!channelId) {
                console.error(`No channel ID provided. Cannot bind active project channel.`);
                return;
            }

            await this.bindProjectDocument(
                `channels/${channelId}`,
                'active_project_channel'
            );

            await this.bindActiveProjectChannelMembers(channelId);
        },
        async bindActiveProjectChannelMembers(channelId: string) {
            const stateKey = `active_project_channel_members`;
            await this.bindProjectCollectionNested<ChannelMember[]>(
                `channels/${channelId}/members`,
                stateKey,
                [['createdAt', 'asc']]
            );
            this.resetActiveProjectChannelMembersTmp();
        },
        resetActiveProjectChannelMembersTmp() {
            // Use member.id (document ID) instead of member.uid for consistency
            this.setState({ key: 'active_project_channel_members_tmp', value: this.active_project_channel_members.map((member: any) => member.id) });
        },
        async bindActiveProjectChannelMembership(channelId: string, memberId: string) {
            if (!channelId) {
                console.error(`No channel ID provided. Cannot bind active project channel.`);
                return;
            }
            if (!memberId) {
                console.error(`No member ID provided. Cannot bind active project channel membership.`);
                return;
            }

            await this.bindProjectDocument(
                `channels/${channelId}/members/${memberId}`,
                'active_project_channel_membership'
            );
        },
        async bindProjectEvents() {
            await this.bindProjectCollection<ProjectEvent[]>(
                'events',
                'project_events',
                [['createdAt', 'asc']],
            );
        },
        async bindActiveProjectEvent(eventId: string, usePolling: boolean = false) {
            if (!eventId) {
                console.error(`No event ID provided. Cannot bind active project event.`);
                return;
            }

            await this.bindProjectDocument(
                `events/${eventId}`,
                'active_project_event',
                usePolling
            );

            // Use pagination by default for attendees to handle large events
            await this.bindActiveProjectEventAttendees(eventId, 1000);
        },
        async bindActiveProjectEventAttendees(eventId: string, pageSize: number = 1000) {
            const stateKey = `active_project_event_attendees`;
            const collectionPath = `events/${eventId}/attendees`;
            
            console.log(`🎯 [ProjectStore] bindActiveProjectEventAttendees called`, {
                eventId,
                projectId: this.$state.projectId,
                fullPath: `projects/${this.$state.projectId}/${collectionPath}`,
                pageSize
            });

            // For events with many attendees, use paginated loading
            // Note: We don't require sorting by createdAt since older attendees might not have this field
            await this.bindProjectCollectionNestedWithPagination<EventAttendeeSchema[]>(
                collectionPath,
                stateKey,
                undefined, // No sorting required - documents without createdAt would be excluded
                pageSize
            );
            
            console.log(`🎯 [ProjectStore] bindActiveProjectEventAttendees completed, current count:`, 
                this.$state.active_project_event_attendees?.length ?? 0);
            
            this.resetActiveProjectChannelMembersTmp();
        },
        /**
         * Fetches invoices for a specific event attendee.
         * Invoices are stored in the attendees/{uid}/invoices subcollection.
         * 
         * @param eventId - The event ID
         * @param attendeeUid - The attendee's user ID
         * @returns Promise<void>
         */
        async fetchEventAttendeeInvoices(eventId: string, attendeeUid: string): Promise<void> {
            if (!this.$state.projectId || this.$state.projectId.trim() === '') {
                console.error('[ProjectStore] No valid project ID. Cannot fetch attendee invoices.');
                return;
            }

            if (!attendeeUid || attendeeUid.trim() === '') {
                console.error('[ProjectStore] No valid attendee UID. Cannot fetch invoices.');
                return;
            }

            console.log('[ProjectStore] Fetching invoices for attendee', {
                projectId: this.$state.projectId,
                eventId,
                attendeeUid,
            });

            try {
                const invoicesRef = collection(
                    firestore, 
                    'projects', 
                    this.$state.projectId, 
                    'events', 
                    eventId, 
                    'attendees', 
                    attendeeUid, 
                    'invoices'
                );
                const q = query(invoicesRef, firestoreOrderBy('createdAt', 'desc'));
                
                const snapshot = await (await import('firebase/firestore')).getDocs(q);
                const invoices = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                console.log(`[ProjectStore] Fetched ${invoices.length} invoices for attendee ${attendeeUid}`);

                // Update state with invoices keyed by attendee uid
                this.$state.active_project_event_attendee_invoices = {
                    ...this.$state.active_project_event_attendee_invoices,
                    [attendeeUid]: invoices as any,
                };
            } catch (error) {
                console.error('[ProjectStore] Error fetching attendee invoices:', error);
            }
        },
        /**
         * Gets cached invoices for an attendee, or fetches them if not cached.
         * 
         * @param eventId - The event ID
         * @param attendeeUid - The attendee's user ID
         * @returns The invoices array or an empty array
         */
        async getEventAttendeeInvoices(eventId: string, attendeeUid: string) {
            const cached = this.$state.active_project_event_attendee_invoices[attendeeUid];
            if (cached) {
                return cached;
            }
            await this.fetchEventAttendeeInvoices(eventId, attendeeUid);
            return this.$state.active_project_event_attendee_invoices[attendeeUid] || [];
        },
        async bindProjectMail() {
            // Validate that we have a valid projectId
            if (!this.projectId || this.projectId.trim() === '') {
                console.error(`No valid project ID. Cannot bind project mail.`);
                return;
            }

            const boundKey = 'project_mail';
            // Check if we have an active listener (unsubscribe function) rather than just boundItems
            if (this.unsubscribes.has(boundKey)) {
                console.log(`[ProjectStore] Mail collection already has active listener, skipping`);
                return;
            }

            // Check if we're in demo mode
            if (this.isProjectInDemoMode()) {
                console.log(`[ProjectStore] Demo mode detected, loading static mail data`);
                try {
                    // Process timestamp objects in mock data
                    const processTimestamps = (obj: any): any => {
                        if (obj === null || obj === undefined) return obj;

                        if (Array.isArray(obj)) {
                            return obj.map(item => processTimestamps(item));
                        }

                        if (typeof obj === 'object') {
                            if (obj.__type === 'timestamp') {
                                return Timestamp.fromMillis(obj.seconds * 1000);
                            }

                            const result: any = {};
                            for (const key in obj) {
                                result[key] = processTimestamps(obj[key]);
                            }
                            return result;
                        }

                        return obj;
                    };

                    // Load demo mail data
                    const { default: demoMailData } = await import('@/static/demo-user/mail.json');
                    console.log(`[ProjectStore] Loaded demo mail JSON, raw data:`, demoMailData);
                    const processedMail = processTimestamps(demoMailData);

                    // Keep Timestamp objects for createdAt/updatedAt to match MailMessageExtended type
                    const mails = processedMail.map((mail: any) => ({
                        ...mail,
                        createdAt: mail.createdAt instanceof Timestamp ? mail.createdAt : Timestamp.fromDate(new Date(mail.createdAt)),
                        updatedAt: mail.updatedAt instanceof Timestamp ? mail.updatedAt : Timestamp.fromDate(new Date(mail.updatedAt)),
                    })) as MailMessageExtended[];

                    console.log(`[ProjectStore] Processed demo mail:`, mails);
                    this.setState({ key: 'project_mail', value: mails });
                    this.boundItems.add(boundKey);
                    console.log(`[ProjectStore] Demo mail data loaded: ${mails.length} messages`);
                } catch (error) {
                    console.error(`[ProjectStore] Error loading demo mail data:`, error);
                    this.setState({ key: 'project_mail', value: [] });
                    this.boundItems.add(boundKey);
                }
                return;
            }

            // Regular implementation for non-demo mode
            const collectionRef = collection(firestore, 'projects', this.projectId, 'mail');
            const q = query(collectionRef, firestoreOrderBy('createdAt', 'asc'));

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const mails = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...(data as any),
                        createdAt: data.createdAt ? (data.createdAt as Timestamp).toDate() : new Date(),
                    } as MailMessageExtended;
                });

                this.setState({ key: 'project_mail', value: mails });
            });

            this.unsubscribes.set(boundKey, unsubscribe);
            this.boundItems.add(boundKey);
        },
        async bindProjectSubscriptions() {
            await this.bindProjectCollection<ProjectSubscription[]>(
                'subscriptions',
                'project_subscriptions',
                [['createdAt', 'desc']]
            );
        },
        async bindProjectPaymentsProducts() {
            // Check if we're in demo mode
            if (this.isProjectInDemoMode()) {
                // In demo mode, we just mark this collection as bound
                this.boundItems.add('project_payments_products');
                return;
            }

            // Regular implementation for non-demo mode
            await this.bindProjectCollection<Product[]>(
                'payments',
                'project_payments_products',
                [['createdAt', 'asc']],
                'object',  // The field you want to filter by
                '==', // The filter operator
                'product'      // The value you want to filter for
            );
            // After binding the payments, bind the nested collections
            await this.bindNestedCollectionsForPayments();
        },
        async bindProjectPaymentsShippingRates() {
            // Check if we're in demo mode
            if (this.isProjectInDemoMode()) {
                // In demo mode, we just mark this collection as bound
                this.boundItems.add('project_payments_shipping_rates');
                return;
            }

            // Regular implementation for non-demo mode
            await this.bindProjectCollection<StripeShippingRate[]>(
                'payments',
                'project_payments_shipping_rates',
                [['createdAt', 'asc']],
                'object',  // The field you want to filter by
                '==', // The filter operator
                'shipping_rate'      // The value you want to filter for
            );

            // After binding the shipping rates, affiliate shipping rates with products
            await this.affiliateShippingRatesWithProducts();
        },
        async bindProjectPaymentBuyButtons() {
            // Check if we're in demo mode
            if (this.isProjectInDemoMode()) {
                // In demo mode, we just mark this collection as bound
                this.boundItems.add('project_payments_buy_buttons');
                return;
            }

            // Regular implementation for non-demo mode
            await this.bindProjectCollection<ExtendedBuyButtonData[]>(
                'payments',
                'project_payments_buy_buttons',
                [['createdAt', 'asc']],
                'object',  // The field you want to filter by
                '==', // The filter operator
                'buy_button'      // The value you want to filter for
            );
        },
        async bindNestedCollectionsForPayments() {
            // Skip if in demo mode
            if (this.isProjectInDemoMode()) {
                return;
            }

            const products = this.$state.project_payments_products as Product[];
            if (products && Array.isArray(products) && products.length > 0) {
                const promises = products.map(async (product) => {
                    await this.bindNestedPrices(product);
                    await this.bindNestedPaymentLinks(product);
                    await this.bindNestedImages(product);
                });
                await Promise.all(promises);
            }
        },
        async bindNestedPrices(product: Product) {
            const stateKey = `project_payments_prices.${product.id}`;
            await this.bindProjectCollectionNested<Price[]>(
                `payments/${product.id}/prices`,
                stateKey, // Unique state key for each product's prices
                [['createdAt', 'asc']]
            );
        },
        async bindNestedPaymentLinks(product: Product) {
            const stateKey = `project_payments_paymentLinks.${product.id}`;
            await this.bindProjectCollectionNested<PaymentLink[]>(
                `payments/${product.id}/payment_links`,
                stateKey, // Unique state key for each product's payment links
                [['createdAt', 'asc']]
            );
        },
        async bindNestedImages(product: Product) {
            const stateKey = `project_payments_images.${product.id}`;
            await this.bindProjectCollectionNested<CloudinaryAsset[]>(
                `payments/${product.id}/images`,
                stateKey, // Unique state key for each product's payment links
                [['createdAt', 'asc']]
            );
        },
        async bindSpecificProjectInfo(path: string, stateId: string) {
            const validKeys = Object.keys(defaultState) as Array<keyof ProjectState>;
            if (!validKeys.includes(stateId as keyof ProjectState)) {
                console.error(`${stateId} is not a valid key of ProjectState`);
                return;
            }
            await this.bindProjectDocument(path, stateId as keyof ProjectState);
        },
        async affiliateShippingRatesWithProducts() {
            // Skip if in demo mode
            if (this.isProjectInDemoMode()) {
                return;
            }

            const productMap: Record<string, StripeMegaProduct> = {};

            // Ensure products are of type StripeMegaProduct
            (this.$state.project_payments_products as StripeMegaProduct[]).forEach((product: StripeMegaProduct) => {
                productMap[product.id] = product;
            });

            // Ensure shipping rates are of type StripeShippingRate
            (this.$state.project_payments_shipping_rates as StripeShippingRate[]).forEach((rate: StripeShippingRate) => {
                if (rate.products && Array.isArray(rate.products) && rate.products.length > 0) {
                    (rate.products as { id: string }[]).forEach((productItem) => {
                        const product = productMap[productItem.id];
                        if (product) {
                            if (!product.shippingRates) {
                                product.shippingRates = [];
                            }
                            product.shippingRates.push(rate);
                        }
                    });
                }
            });
        },
        setProjectGroupField() {
            const groupField = this.$state.project_user_fields.find(field => field.group === true);
            if (groupField) {
                this.setState({ key: 'project_group_field', value: groupField });
            }
        },
        resetProjectGroupField() {
            this.setState({ key: 'project_group_field', value: null });
        },
        /**
         * Reset all active bulk import session related data
         * Used when switching between sessions to ensure no stale data remains
         */
        resetActiveBulkImportSessionData() {
            // Reset the primary session data
            this.reset('active_bulk_import_session');

            // Reset all related collections
            this.reset('active_bulk_import_session_users');
            this.reset('active_bulk_import_session_errors');
            this.reset('active_bulk_import_session_profile_images');
            this.reset('active_bulk_import_session_user_pairs');
            this.reset('active_bulk_import_session_users_not_in_pair');
            this.reset('active_bulk_import_session_images_not_in_pair');

            // Reset navigation state
            this.setState({
                key: 'bulk_import_sessions_navigation_button_disabled',
                value: {
                    previous: false,
                    next: true
                }
            });
        },
        // Helper method to check if bulk import session collections are bound
        isActiveBulkImportSessionCollectionsBound(): boolean {
            const collectionKeys = [
                'active_bulk_import_session_users',
                'active_bulk_import_session_profile_images',
                'active_bulk_import_session_user_pairs',
                'active_bulk_import_session_users_not_in_pair',
                'active_bulk_import_session_images_not_in_pair',
                'active_bulk_import_session_errors'
            ];
            // Check if ALL collections are bound, not just any of them
            return collectionKeys.every(key => this.$state.boundItems.has(key));
        },

        async bindActiveBulkImportSessionCollections(pageSize: number = 100) {
            if (this.active_bulk_import_session) {
                const sessionId = this.active_bulk_import_session.id;
                console.log(`[ProjectStore] Binding collections for session: ${sessionId}`);

                // Users: use paginated binding to avoid loading large collections at once
                await this.bindProjectCollectionNestedWithPagination<any[]>(
                    `import_sessions/${sessionId}/users`,
                    'active_bulk_import_session_users',
                    [['import_status', 'desc']],
                    pageSize,
                    undefined,
                    undefined,
                    undefined,
                    // Custom sort: failed/conflict first, then processing/pending, then completed
                    (data) => {
                        const order: Record<string, number> = {
                            failed: 0,
                            conflict: 0,
                            processing: 1,
                            publishing: 1,
                            pending: 2,
                            complete: 3,
                            completed: 3,
                        };
                        return [...data].sort((a: any, b: any) => {
                            // Prefer server-provided numeric rank for stable ordering across pages
                            const aRank = Number.isFinite((a as any)?.import_status_rank)
                                ? Number((a as any).import_status_rank)
                                : (order[String(a?.import_status || '').toLowerCase()] ?? 4);
                            const bRank = Number.isFinite((b as any)?.import_status_rank)
                                ? Number((b as any).import_status_rank)
                                : (order[String(b?.import_status || '').toLowerCase()] ?? 4);
                            if (aRank !== bRank) return aRank - bRank;
                            return String((a as any).id || '').localeCompare(String((b as any).id || ''));
                        });
                    }
                );

                // Also bind metadata document with total counts if present
                try {
                    const metaPath = `import_sessions/${sessionId}`;
                    await this.bindProjectDocument(metaPath, 'active_bulk_import_session');
                } catch (e) {
                    console.warn('[ProjectStore] Could not bind session metadata for totals');
                }

                await this.bindProjectCollection<any[]>(
                    `import_sessions/${sessionId}/profile_images`,
                    'active_bulk_import_session_profile_images',
                    [['createdAt', 'asc']]
                );

                await this.bindProjectCollection<any[]>(
                    `import_sessions/${sessionId}/pairs`,
                    'active_bulk_import_session_user_pairs',
                    [['createdAt', 'asc']]
                );

                await this.bindProjectCollection<any[]>(
                    `import_sessions/${sessionId}/users_not_in_pair`,
                    'active_bulk_import_session_users_not_in_pair',
                    [['createdAt', 'asc']]
                );

                await this.bindProjectCollection<any[]>(
                    `import_sessions/${sessionId}/images_not_in_pair`,
                    'active_bulk_import_session_images_not_in_pair',
                    [['createdAt', 'asc']]
                );

                await this.bindProjectCollection<any[]>(
                    `import_sessions/${sessionId}/users`,
                    'active_bulk_import_session_users',
                    [['createdAt', 'asc']]
                );

                await this.bindProjectCollection<any[]>(
                    `import_sessions/${sessionId}/errors`,
                    'active_bulk_import_session_errors',
                    [['createdAt', 'asc']]
                );

                console.log(`[ProjectStore] All collections bound for session: ${sessionId}`);
            }
        },

        async loadMoreActiveBulkImportSessionUsers(): Promise<boolean> {
            const stateKey = 'active_bulk_import_session_users';
            const paginationKey = `${stateKey}_pagination`;
            const paginationData = this.getNestedState(paginationKey);
            const sessionId = this.active_bulk_import_session?.id;

            if (!sessionId) {
                console.error('[ProjectStore] No active import session for loadMoreActiveBulkImportSessionUsers');
                return false;
            }

            if (!paginationData || !paginationData.hasMore || !paginationData.lastDoc) {
                return false;
            }

            try {
                let firebaseRef: CollectionReference | Query = collection(
                    firestore,
                    'projects',
                    this.$state.projectId,
                    `import_sessions/${sessionId}/users`
                );

                // Sorting must match initial load
                firebaseRef = query(firebaseRef, firestoreOrderBy('import_status_rank', 'asc'));
                firebaseRef = query(firebaseRef, firestoreOrderBy('id', 'asc'));
                firebaseRef = query(firebaseRef, startAfter(paginationData.lastDoc));
                firebaseRef = query(firebaseRef, firestoreLimit(paginationData.pageSize));

                return new Promise((resolve, reject) => {
                    const unsubscribe = onSnapshot(
                        firebaseRef,
                        (snapshot) => {
                            const newData: any[] = [];
                            snapshot.forEach((doc) => {
                                newData.push({ id: doc.id, ...doc.data() });
                            });

                            if (newData.length > 0) {
                                const existingData = this.getNestedState(stateKey) || [];
                                const combined = [...existingData, ...newData];
                                this.setNestedState(stateKey, combined);

                                this.setNestedState(paginationKey, {
                                    ...paginationData,
                                    hasMore: snapshot.docs.length === paginationData.pageSize,
                                    lastDoc: snapshot.docs[snapshot.docs.length - 1] || paginationData.lastDoc,
                                    currentCount: combined.length
                                });
                                // Update total if session doc exposes users_count
                                const total = (this.active_bulk_import_session as any)?.users_count;
                                if (typeof total === 'number') {
                                    this.setState({ key: 'active_bulk_import_session_users_total', value: total });
                                }
                            } else {
                                // No new data means no more pages
                                this.setNestedState(paginationKey, {
                                    ...paginationData,
                                    hasMore: false
                                });
                            }

                            unsubscribe();
                            resolve(newData.length > 0);
                        },
                        (error) => {
                            console.error('[ProjectStore] Error loading more import session users:', error);
                            unsubscribe();
                            reject(error);
                        }
                    );
                });
            } catch (e) {
                console.error('[ProjectStore] Exception in loadMoreActiveBulkImportSessionUsers:', e);
                return false;
            }
        },

        async bindProjectOrders() {
            // Check if we're in demo mode
            if (this.isProjectInDemoMode()) {
                // In demo mode, we just mark this collection as bound
                // The data is already loaded in the user store
                this.boundItems.add('project_orders');
                return;
            }

            // Regular implementation for non-demo mode
            await this.bindProjectCollection<Order[]>(
                'orders',
                'project_orders',
                [['createdAt', 'asc']]
            );
            await this.bindNestedCollectionsForOrders()
        },

        async bindNestedCollectionsForOrders() {
            // Skip if in demo mode
            if (this.isProjectInDemoMode()) {
                return;
            }

            const orders = this.$state.project_orders as Order[];
            if (orders && Array.isArray(orders) && orders.length > 0) {
                const promises = orders.map(async (order) => {
                    await this.bindNestedOrderItems(order);
                });
                await Promise.all(promises);
            }
        },

        async bindNestedOrderItems(order: Order) {
            // Skip if in demo mode
            if (this.isProjectInDemoMode()) {
                return;
            }

            const stateKey = `project_orders_items.${order.id}`;
            await this.bindProjectCollectionNested<OrderCartItem[]>(
                `orders/${order.id}/items`,
                stateKey, // Unique state key for each product's payment links
                [['createdAt', 'asc']]
            );
        },

        initializeWatchers() {
            // If watchers are already initialized, do nothing
            if (this.watchersInitialized) {
                return;
            }

            // Watch for changes in project_user_fields
            watch(() => this.$state.project_user_fields, (newFields, oldFields) => {
                if (newFields !== oldFields) {
                    // Clear transformation caches when project user fields change
                    console.log('🔄 Project user fields changed, clearing all caches');

                    // Import and call cache clearing functions
                    import('@/utils/projectUserFields').then(({ clearFieldProcessingCache }) => {
                        clearFieldProcessingCache();
                    });
                    import('@/utils/transformations').then(({ clearTransformationCache }) => {
                        clearTransformationCache();
                    });
                    // Note: User data cache is cleared per-composable instance, so we don't clear it globally here

                    const groupFieldChanged = newFields.some((newField, index) => {
                        const oldField = oldFields[index] || {};
                        return newField.group !== oldField.group;
                    });
                    if (groupFieldChanged) {
                        this.resetProjectGroupField();
                        this.setProjectGroupField();
                    }
                }
            }, {
                deep: true // This ensures the watcher will detect nested changes in the object/array
            });

            // Watch for changes in active_bulk_import_session_users
            watch(() => this.$state.active_bulk_import_session_users, async () => {
                // Only update loading state for incomplete sessions
                if (this.active_bulk_import_session?.status !== 'Complete') {
                    // Set error loading state to true (errors are bound from Firebase)
                    useApplicationStore().setState({ key: 'importUsersErrorsLoaded', value: true });
                } else {
                    useApplicationStore().setState({ key: 'importUsersErrorsLoaded', value: true });
                }
            }, {
                deep: true
            });

            // Also watch for changes in user pairs (for image pairing functionality)
            watch(() => this.active_bulk_import_session_user_pairs, async (newPairs, oldPairs) => {
                // Only update the errors loaded state without trying to prepare users
                if (newPairs !== oldPairs && this.active_bulk_import_session_users && this.active_bulk_import_session?.status !== 'Complete') {
                    // Set error loading state to false first
                    useApplicationStore().setState({ key: 'importUsersErrorsLoaded', value: false });

                    // Now that we have the prepareUsers method, we can use it properly
                    // Set error loading state back to true when done
                    useApplicationStore().setState({ key: 'importUsersErrorsLoaded', value: true });
                } else {
                    useApplicationStore().setState({ key: 'importUsersErrorsLoaded', value: true });
                }
            }, {
                deep: true // This ensures the watcher will detect nested changes in the object/array
            });

            // Watch for changes in active_bulk_import_session_errors directly
            // This ensures the displayed count is always in sync
            watch(() => this.$state.active_bulk_import_session_errors, (newErrors, oldErrors) => {
                const applicationStore = useApplicationStore();

                // Set errors as loaded when the Firebase binding provides data
                if (newErrors !== oldErrors) {
                    applicationStore.setState({ key: 'importUsersErrorsLoaded', value: true });
                }

                // Use the filtered count getter to update displayed count
                const errorCount = this.filteredImportErrorCount;
                this.setState({
                    key: 'displayed_import_error_count',
                    value: errorCount
                });

                if (errorCount > 0) {
                    console.log('[ProjectStore] Errors detected:', errorCount, 'out of', newErrors?.length || 0);
                }
            }, {
                deep: true
            });

            // Keep a real-time mirrored users_total from the session doc for badges
            watch(
                () => (this.active_bulk_import_session as any)?.users_count,
                (newTotal) => {
                    if (typeof newTotal === 'number' && newTotal >= 0) {
                        this.setState({ key: 'active_bulk_import_session_users_total', value: newTotal });
                    }
                },
                { immediate: true }
            );

            /**
             * NEW WATCH: Watch for changes to `project`. 
             * If the new project has an `info` property,
             * automatically set `projectInfo` to `project.info`.
             */
            watch(() => this.$state.project, (newProject, oldProject) => {
                if (newProject !== oldProject) {
                    if (newProject && newProject.info) {
                        // If project.info exists, we set projectInfo to that
                        this.setState({ key: 'projectInfo', value: newProject.info });
                    } else {
                        // Otherwise, we can reset or nullify projectInfo
                        this.setState({ key: 'projectInfo', value: null });
                    }
                }
            }, {
                deep: true
            });

            this.setState({ key: 'watchersInitialized', value: true })
        },

        /**
         * Initialize spreadsheet permission watcher to notify when the import
         * spreadsheet becomes locked. Also notifies immediately if the current
         * status is locked when the watcher is set up.
         */
        initializeSpreadsheetLockWatcher() {
            // Prevent duplicate initialization by keying off a bound flag
            const boundKey = 'spreadsheet_lock_watcher' as keyof ProjectState;
            if ((this.$state.boundItems as Set<string>).has(boundKey as unknown as string)) {
                return;
            }

            const applicationStore = useApplicationStore();

            let lastStatus: boolean | null = null;
            watch(
                () => this.importSpreadsheetIsPublic,
                (isPublic, wasPublic) => {
                    // Notify when it transitions to locked, or if initially locked
                    const becameLocked = wasPublic !== false && isPublic === false;
                    const initiallyLocked = lastStatus === null && isPublic === false;

                    if (becameLocked || initiallyLocked) {
                        applicationStore.showEnhancedNotification({
                            title: 'Spreadsheet is locked',
                            message: 'The import spreadsheet is currently locked. Unlock it to continue editing or viewing.',
                            theme: 'warning',
                            priority: 'high',
                            sticky: false,
                            dismissible: true
                        });
                    }

                    lastStatus = isPublic;
                },
                { immediate: true }
            );

            (this.$state.boundItems as Set<string>).add(boundKey as unknown as string);
        },

        // New action to unbind collection or document
        unbind(boundKey: keyof ProjectState) {
            if (this.$state.boundItems.has(boundKey)) {
                const unsubscribe = this.$state.unsubscribes.get(boundKey as string);
                if (unsubscribe) {
                    unsubscribe(); // Call the unsubscribe function to stop listening to Firestore changes
                    this.$state.unsubscribes.delete(boundKey as string); // Remove the unsubscribe function from the map
                }
                this.$state.boundItems.delete(boundKey); // Remove the bound item from the set
                this.setState({ key: boundKey, value: defaultState[boundKey] }); // Reset the state
                console.log(`Unbound ${boundKey} successfully.`);
            } else {
                console.log(`${boundKey} is not currently bound.`);
            }
        },

        // Add this utility method
        isProjectInDemoMode(): boolean {
            // Check if project is in demo mode (inline logic from isDemoMode)
            return this.projectId === 'demo' ||
                (typeof this.projectId === 'string' && this.projectId.startsWith('demo_'));
        },

        async setDemoData() {
            // Process timestamp objects in mock data
            const processTimestamps = (obj: any): any => {
                if (obj === null || obj === undefined) return obj;

                if (Array.isArray(obj)) {
                    return obj.map(item => processTimestamps(item));
                }

                if (typeof obj === 'object') {
                    if (obj.__type === 'timestamp') {
                        return Timestamp.fromMillis(obj.seconds * 1000);
                    }

                    const result: any = {};
                    for (const key in obj) {
                        result[key] = processTimestamps(obj[key]);
                    }
                    return result;
                }

                return obj;
            };

            // Set mock project products data from the imported JSON files
            const mockProducts = processTimestamps(demoOrdersData.projectProducts || []);
            const mockShippingRates = processTimestamps(demoOrdersData.projectShippingRates || []);
            const mockBuyButtons = processTimestamps(demoOrdersData.projectBuyButtons || []);

            // Process marketplace products and add them to project products
            const { default: demoMarketplaceProductsData } = await import('@/static/demo-user/marketplace.json');
            const processedMarketplaceProducts = processTimestamps(demoMarketplaceProductsData);

            // Process donation products and add them to project products
            const processedDonationProducts = processTimestamps(demoDonationProducts);

            // Combine all products for the project store
            // Add product_type field to distinguish between marketplace and donation products
            const marketplaceWithType = processedMarketplaceProducts.map((product: any) => ({
                ...product,
                product_type: { id: 'marketplace', name: 'Marketplace' }
            }));

            const donationsWithType = processedDonationProducts.map((product: any) => ({
                ...product,
                product_type: { id: 'donation', name: 'Donation' }
            }));

            // Combine all products
            const allProducts = [
                ...mockProducts,
                ...marketplaceWithType,
                ...donationsWithType
            ];

            this.setState({ key: 'project_payments_products', value: allProducts });
            this.setState({ key: 'project_payments_shipping_rates', value: mockShippingRates as any });
            this.setState({ key: 'project_payments_buy_buttons', value: mockBuyButtons as any });

            // Create nested structure for prices and payment links
            const pricesMap: Record<string, any[]> = {};
            const paymentLinksMap: Record<string, any[]> = {};
            const imagesMap: Record<string, any[]> = {};

            // Process marketplace products
            marketplaceWithType.forEach((product: any) => {
                if (product.prices) {
                    pricesMap[product.id] = product.prices;
                }
                if (product.paymentLinks) {
                    paymentLinksMap[product.id] = product.paymentLinks;
                }
                if (product.images) {
                    imagesMap[product.id] = product.images.map((url: string) => ({
                        url,
                        public_id: url.split('/').pop() || ''
                    }));
                }
            });

            // Process donation products
            donationsWithType.forEach((product: any) => {
                if (product.prices) {
                    pricesMap[product.id] = product.prices;
                }
                if (product.paymentLinks) {
                    paymentLinksMap[product.id] = product.paymentLinks;
                }
                if (product.images) {
                    imagesMap[product.id] = product.images.map((url: string) => ({
                        url,
                        public_id: url.split('/').pop() || ''
                    }));
                }
            });

            this.setState({ key: 'project_payments_prices', value: pricesMap });
            this.setState({ key: 'project_payments_paymentLinks', value: paymentLinksMap });
            this.setState({ key: 'project_payments_images', value: imagesMap });

            // Mark these collections as already bound to prevent actual binding attempts
            this.boundItems.add('project_payments_products');
            this.boundItems.add('project_payments_shipping_rates');
            this.boundItems.add('project_payments_buy_buttons');
            this.boundItems.add('project_payments_prices');
            this.boundItems.add('project_payments_paymentLinks');
            this.boundItems.add('project_payments_images');
        },

        // Add a method to set only marketplace products for preview/demo
        setDemoMarketplaceProducts() {
            // Import here to avoid circular dependencies
            import('@/static/demo-user/marketplace.json').then((demoMarketplaceProducts) => {
                // Process timestamp objects in mock data
                const processTimestamps = (obj: any): any => {
                    if (obj === null || obj === undefined) return obj;

                    if (Array.isArray(obj)) {
                        return obj.map(item => processTimestamps(item));
                    }

                    if (typeof obj === 'object') {
                        if (obj.__type === 'timestamp') {
                            return Timestamp.fromMillis(obj.seconds * 1000);
                        }

                        const result: any = {};
                        for (const key in obj) {
                            result[key] = processTimestamps(obj[key]);
                        }
                        return result;
                    }

                    return obj;
                };

                // Process marketplace products
                const processedMarketplaceProducts = processTimestamps(demoMarketplaceProducts.default);

                // Add product_type field to marketplace products
                const marketplaceWithType = processedMarketplaceProducts.map((product: any) => ({
                    ...product,
                    product_type: { id: 'marketplace', name: 'Marketplace' }
                }));

                // Set in the project store
                this.setState({ key: 'project_payments_products', value: marketplaceWithType });

                // Create nested structure for prices and payment links
                const pricesMap: Record<string, any[]> = {};
                const paymentLinksMap: Record<string, any[]> = {};
                const imagesMap: Record<string, any[]> = {};

                // Process products
                marketplaceWithType.forEach((product: any) => {
                    if (product.prices) {
                        pricesMap[product.id] = product.prices;
                    }
                    if (product.paymentLinks) {
                        paymentLinksMap[product.id] = product.paymentLinks;
                    }
                    if (product.images) {
                        imagesMap[product.id] = product.images.map((url: string) => ({
                            url,
                            public_id: url.split('/').pop() || ''
                        }));
                    }
                });

                this.setState({ key: 'project_payments_prices', value: pricesMap });
                this.setState({ key: 'project_payments_paymentLinks', value: paymentLinksMap });
                this.setState({ key: 'project_payments_images', value: imagesMap });
            });
        },

        // Add method to clear demo products
        clearDemoProducts() {
            if (!this.isProjectInDemoMode()) {
                this.setState({ key: 'project_payments_products', value: [] });
                this.setState({ key: 'project_payments_prices', value: {} });
                this.setState({ key: 'project_payments_paymentLinks', value: {} });
                this.setState({ key: 'project_payments_images', value: {} });
            }
        },
        async bindProjectGroups() {
            // Check if we have an active listener (unsubscribe function) rather than just boundItems
            if (this.unsubscribes.has('project_groups')) {
                console.log(`[ProjectStore] Groups collection already has active listener, skipping`);
                return;
            }

            const projectId = this.projectId;
            if (!projectId) return;

            const applicationStore = useApplicationStore();

            try {
                const groupsRef = collection(firestore, 'projects', projectId, 'groups');

                const unsubscribe = onSnapshot(groupsRef, (snapshot) => {
                    const groups: Group[] = [];
                    snapshot.forEach((doc) => {
                        const group = { id: doc.id, ...doc.data() } as Group;
                        groups.push(group);
                    });

                    this.project_groups = groups;
                    console.log(`[ProjectStore] Groups loaded: ${groups.length} groups`);
                }, (error) => {
                    console.error('Error fetching project groups:', error);
                    applicationStore.showNotification({
                        title: 'Error',
                        message: 'Failed to load groups',
                        theme: 'danger',
                        autoClose: true,
                        isOpen: true
                    });
                });

                this.unsubscribes.set('project_groups', unsubscribe);
                this.boundItems.add('project_groups');
            } catch (error) {
                console.error('Exception when binding project groups:', error);
                applicationStore.showNotification({
                    title: 'Error',
                    message: 'Failed to load groups',
                    theme: 'danger',
                    autoClose: true,
                    isOpen: true
                });
            }
        },

        /**
         * Binds an active project group and its members
         */
        bindActiveProjectGroup(groupId: string) {
            if (!groupId) {
                this.active_project_group = null;
                return;
            }

            const projectId = this.projectId;
            if (!projectId) return;

            // Unbind previous active group if needed
            if (this.unsubscribes.has('active_project_group')) {
                const unsubscribe = this.unsubscribes.get('active_project_group');
                if (unsubscribe) unsubscribe();
                this.boundItems.delete('active_project_group');
            }

            const applicationStore = useApplicationStore();

            try {
                const groupRef = doc(firestore, 'projects', projectId, 'groups', groupId);

                const unsubscribe = onSnapshot(groupRef, (doc) => {
                    if (doc.exists()) {
                        this.active_project_group = { id: doc.id, ...doc.data() } as Group;

                        // Bind group members when a group is selected
                        this.bindActiveProjectGroupMembers(groupId);
                    } else {
                        this.active_project_group = null;
                        applicationStore.showNotification({
                            title: 'Error',
                            message: 'The requested group does not exist',
                            theme: 'danger',
                            autoClose: true,
                            isOpen: true
                        });
                    }
                }, (error) => {
                    console.error('Error fetching active project group:', error);
                    applicationStore.showNotification({
                        title: 'Error',
                        message: 'Failed to load group information',
                        theme: 'danger',
                        autoClose: true,
                        isOpen: true
                    });
                });

                this.unsubscribes.set('active_project_group', unsubscribe);
                this.boundItems.add('active_project_group');
            } catch (error) {
                console.error('Exception when binding active project group:', error);
                applicationStore.showNotification({
                    title: 'Error',
                    message: 'Failed to load group information',
                    theme: 'danger',
                    autoClose: true,
                    isOpen: true
                });
            }
        },

        /**
         * Binds the members of the active project group
         */
        bindActiveProjectGroupMembers(groupId: string) {
            if (!groupId) {
                this.active_project_group_members = [];
                return;
            }

            const projectId = this.projectId;
            if (!projectId) return;

            // Unbind previous members if needed
            if (this.unsubscribes.has('active_project_group_members')) {
                const unsubscribe = this.unsubscribes.get('active_project_group_members');
                if (unsubscribe) unsubscribe();
                this.boundItems.delete('active_project_group_members');
            }

            const applicationStore = useApplicationStore();

            try {
                const membersRef = collection(firestore, 'projects', projectId, 'groups', groupId, 'members');

                const unsubscribe = onSnapshot(membersRef, (snapshot) => {
                    const members: GroupMember[] = [];
                    snapshot.forEach((doc) => {
                        const member = { id: doc.id, ...doc.data() } as GroupMember;
                        members.push(member);
                    });

                    this.active_project_group_members = members;
                }, (error) => {
                    console.error('Error fetching group members:', error);
                    applicationStore.showNotification({
                        title: 'Error',
                        message: 'Failed to load group members',
                        theme: 'danger',
                        autoClose: true,
                        isOpen: true
                    });
                });

                this.unsubscribes.set('active_project_group_members', unsubscribe);
                this.boundItems.add('active_project_group_members');
            } catch (error) {
                console.error('Exception when binding group members:', error);
                applicationStore.showNotification({
                    title: 'Error',
                    message: 'Failed to load group members',
                    theme: 'danger',
                    autoClose: true,
                    isOpen: true
                });
            }
        },

        /**
         * Binds the current user's membership in the active group
         */
        bindActiveProjectGroupMembership(groupId: string, userId: string) {
            if (!groupId || !userId) {
                this.active_project_group_membership = null;
                return;
            }

            const projectId = this.projectId;
            if (!projectId) return;

            // Unbind previous membership if needed
            if (this.unsubscribes.has('active_project_group_membership')) {
                const unsubscribe = this.unsubscribes.get('active_project_group_membership');
                if (unsubscribe) unsubscribe();
                this.boundItems.delete('active_project_group_membership');
            }

            const applicationStore = useApplicationStore();

            try {
                const membershipRef = doc(firestore, 'projects', projectId, 'groups', groupId, 'members', userId);

                const unsubscribe = onSnapshot(membershipRef, (doc) => {
                    if (doc.exists()) {
                        this.active_project_group_membership = { id: doc.id, ...doc.data() } as GroupMember;
                    } else {
                        this.active_project_group_membership = null;
                    }
                }, (error) => {
                    console.error('Error fetching group membership:', error);
                    applicationStore.showNotification({
                        title: 'Error',
                        message: 'Failed to load your group membership',
                        theme: 'danger',
                        autoClose: true,
                        isOpen: true
                    });
                });

                this.unsubscribes.set('active_project_group_membership', unsubscribe);
                this.boundItems.add('active_project_group_membership');
            } catch (error) {
                console.error('Exception when binding group membership:', error);
                applicationStore.showNotification({
                    title: 'Error',
                    message: 'Failed to load your group membership',
                    theme: 'danger',
                    autoClose: true,
                    isOpen: true
                });
            }
        },

        /**
         * Toggles the permissions of the current import spreadsheet
         * @returns Promise<boolean> True if operation succeeded
         */
        async toggleImportSpreadsheetPermission() {
            // Get either the active session or first session from the array
            const activeSession = this.active_bulk_import_session ||
                (this.import_sessions.length > 0 ? this.import_sessions[0] : null);

            if (!activeSession?.spreadsheet?.id) {
                console.error('No active spreadsheet found to toggle permissions');
                return false;
            }

            try {
                const projectId = this.projectId;
                if (!projectId) {
                    console.error('No project ID available');
                    return false;
                }

                // Import the toggleSheetPermissions function
                const { toggleSheetPermissions } = await import('@/store/helpers/project-sheets/toggleSheetPermissions');

                // Toggle the permissions
                await toggleSheetPermissions(projectId, activeSession.spreadsheet.id);

                return true;
            } catch (error) {
                console.error('Failed to toggle spreadsheet permissions:', error);
                return false;
            }
        },

        /**
         * Adds a new group to the store without rebinding the entire collection
         * This avoids triggering a full reload when a new group is created
         */
        addNewGroupToStore(newGroup: Group) {
            console.log('[DEBUG] Adding new group to store:', newGroup.name);

            // Check if the group already exists
            if (this.project_groups.some(group => group.id === newGroup.id)) {
                console.log('[DEBUG] Group already exists in store, not adding again');
                return;
            }

            // Add the new group to the array
            this.project_groups.push(newGroup);
            console.log('[DEBUG] Group added to store successfully');
        },
        async bindProjectArchive() {
            await this.bindProjectCollection<ArchiveItem>(
                'archive',
                'project_archive',
                [['created', 'desc']]
            );
        },
    },
});